import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { appOrWebDomain } from 'src/app/pages/friend/friend.helper';
import { environment } from 'src/environments/environment';
import {
  AgentApplyBody,
  CommissionRewardsList,
  CreateUserApplyBody,
  DefaultLink,
  FirendsListParams,
  GetCommissionInviteParams,
  GetCommissionReturnParams,
  GetCommissionTrendParams,
  GetDataViewParams,
  GetList,
  GetListParams,
  GetRelationList,
  GetRelationParams,
  PostCreateLinkBody,
  ReferralRewardsList,
  ReferralsListRepsonse,
  SetUserApplyBody,
  ShareImg,
  ShareQueryParams,
  TopOne,
  TopRankParams,
  TopRankRepsonse,
  UpdateRemarkBody,
  UserApplyListParams,
} from '../interfaces/friend.interface';
import { BaseApi } from './base.api';

@Injectable({
  providedIn: 'root',
})
export class FriendApi extends BaseApi {
  //获取默认推荐链接
  getDefault(): Observable<DefaultLink | null> {
    const url = `${environment.apiUrl}/v1/agent/invite/getdefault`;

    return this.get(url).pipe(
      map((x: any) => {
        if (x.data) {
          const urlStr: string[] = x.data.inviteUrl.split('/');
          const len = urlStr.length;
          const newData = {
            ...x.data,
            inviteUrl: `${appOrWebDomain()}/${urlStr[len - 1]}`,
          };
          return newData;
        } else {
          return null;
        }
      })
    );
  }

  //好友数据总览
  getDataView(params: GetDataViewParams): Observable<DataView> {
    const url = `${environment.apiUrl}/v1/agent/friend/dataview`;
    return this.get(url, params, 60 * 1000).pipe(
      map(
        (x: any) =>
          x?.data || {
            count: 0,
            countDiff: 0,
            profit: 0,
            profitDiff: 0,
            reward: 0,
            trading: 0,
            tradingDiff: 0,
          }
      )
    );
  }
  //好友佣金返还
  getCommissionReturn(params: GetCommissionReturnParams) {
    const url = `${environment.apiUrl}getCommissionRewardList/v1/agent/friend/commissionreturn`;
    return this.get(url, params).pipe(map((x: any) => x?.data || { total: 0, list: [] }));
  }
  //好友推荐返还
  getCommssionInvite(params: GetCommissionInviteParams) {
    const url = `${environment.apiUrl}/v1/agent/friend/commissioninvite`;
    return this.get(url, params).pipe(map((x: any) => x?.data || { list: [], total: 0 }));
  }

  //获取游戏类型
  getGameType() {
    const url = `${environment.apiUrl}/v1/agent/friend/gametypes`;
    return firstValueFrom(this.get(url));
  }

  /**
   * 佣金返还走势
   *
   * @param params 佣金返还走势请求参数
   * @returns
   */
  getCommissionTrend(params: GetCommissionTrendParams) {
    const url = `${environment.apiUrl}/v1/agent/friend/commissiontrend`;
    return firstValueFrom(this.get(url, params));
  }

  //导出佣金返还 暂时不对接
  getExportReturn() {
    const url = `${environment.apiUrl}/v1/agent/friend/exportreturn`;
  }

  //导出推荐返还 暂时不对接
  getExportInvite() {
    const url = `${environment.apiUrl}/v1/agent/friend/exportinvite`;
  }

  //创建推荐链接
  postCreateLink(body: PostCreateLinkBody) {
    const url = `${environment.apiUrl}/v1/agent/invite/create`;
    return firstValueFrom(this.post(url, body));
  }

  /**
   * 推荐链接下好友列表
   *
   * @param params
   * @returns
   */
  getRelation(params: GetRelationParams): Observable<GetRelationList> {
    const url = `${environment.apiUrl}/v1/agent/invite/relation`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x.data;
          const list = data.list.map((item: any) => {
            return {
              ...item,
              time: this.generalService.toFullTimestamp(item.time),
            };
          });
          return {
            ...data,
            list,
          };
        }
        return {
          total: 0,
          list: [],
        };
      })
    );
  }

  /**
   * 推荐链接列表
   *
   * @param params 推荐链接列表参数
   * @returns
   */
  getList(params: GetListParams): Observable<GetList> {
    const url = `${environment.apiUrl}/v1/agent/invite/list`;
    return this.get(url, params).pipe(
      map((x: any) => {
        const list = x.data.list
          .map((list: DefaultLink) => {
            const urlStr: string[] = list.inviteUrl.split('/');
            const len = urlStr.length;
            return {
              ...list,
              inviteUrl: `${appOrWebDomain()}/${urlStr[len - 1]}`,
            };
          })
          .sort((a: DefaultLink, b: DefaultLink) => Number(b.isDefault) - Number(a.isDefault));
        const newData = {
          ...x.data,
          list,
        };
        return newData || { total: 0, list: [] };
      })
    );
  }

  /**
   * 更新推荐链接备注
   *
   * @param params 更新推荐链接备注参数
   * @returns
   */
  updateRemark(params: UpdateRemarkBody): Observable<boolean> {
    const url = `${environment.apiUrl}/v1/agent/invite/updateremark`;
    return this.post(url, params).pipe(map((x: any) => x?.data || false));
  }

  /**
   * 设置为默认推荐链接
   *
   * @param params
   * @param params.inviteCode
   * @returns
   */
  setDefault(params: { inviteCode: string }): Observable<boolean> {
    const url = `${environment.apiUrl}/v1/agent/invite/setdefault`;
    return this.post(url, params).pipe(map((x: any) => x?.data || false));
  }

  //获取转入申请
  getUserApply(): Observable<{}> {
    const url = `${environment.apiUrl}/v1/agent/friend/getuserapply`;
    return this.get(url).pipe(map((x: any) => x?.data || {}));
  }
  //确认转入申请
  setUserApply(body: SetUserApplyBody) {
    const url = `${environment.apiUrl}/v1/agent/friend/setuserapply`;
    return firstValueFrom(this.post(url, body));
  }
  //联盟代理申请
  agentApply(params: AgentApplyBody) {
    const url = `${environment.apiUrl}/v1/agent/friend/agentapply`;
    return firstValueFrom(this.post(url, params));
  }
  /**
   * 当前登录是否代理
   *
   * @returns boolean 判断用户是否已经加入联盟
   */
  getIsAgent(): Observable<boolean> {
    const url = `${environment.apiUrl}/v1/agent/friend/isagent`;
    return this.get(url).pipe(map((x: any) => x?.data || false));
  }
  /**
   * 转入申请查询
   *
   * @param params
   * @returns
   */
  getUserApplyList(params: UserApplyListParams) {
    const url = `${environment.apiUrl}/v1/agent/friend/userapplylist`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x.data;
          const list = data.list.map((item: any) => {
            return {
              ...item,
              createTime: this.generalService.toFullTimestamp(item.createTime),
              regTime: this.generalService.toFullTimestamp(item.regTime),
            };
          });
          return {
            ...data,
            list,
          };
        }
        return {
          list: [],
          total: 0,
        };
      })
    );
  }
  // 创建转入申请
  createUserApply(body: CreateUserApplyBody) {
    const url = `${environment.apiUrl}/v1/agent/friend/userapplycreate`;
    return firstValueFrom(this.post(url, body));
  }

  /**
   * 查询转入申请
   *
   * @param params 申请转入发送的参数
   * @returns
   */
  createUserApplyCheck(params: CreateUserApplyBody) {
    const url = `${environment.apiUrl}/v1/agent/friend/userapplycheck`;
    return this.post(url, params).pipe(map((x: any) => x?.data));
  }
  // 创建转入申请 图片
  // createUpLoadUrl(fileName?: string) {
  //   const url = `${environment.apiUrl}/v1/resource/upload/createuploadurl`;
  //   const params: any = {
  //     type: 'Agent',
  //     fileName: fileName,
  //   };
  //   return firstValueFrom(this.post(url, params));
  // }
  // 代理申请状态
  agentApplyStatus() {
    const url = `${environment.apiUrl}/v1/agent/friend/agentapplystatus`;
    return firstValueFrom(this.get(url));
  }

  //获取分享图片
  getShareImg(params: ShareImg) {
    const url = `${environment.apiUrl}/v1/agent/invite/getshareimage`;
    return firstValueFrom(this.get(url, params));
  }

  /**@getCommissionTopYesterday 获取昨日排名*/
  getCommissionTopYesterday(params: TopRankParams): Observable<TopRankRepsonse> {
    const url = `${environment.apiUrl}/v1/agent/friend/commissiontopyesterday`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x?.data as TopRankRepsonse;
          const list = data.list
            .map(info => ({
              ...info,
              avatar: this.avatartDefault.transform(info.avatar),
              name: info.name.length > 0 ? info.name : this.localeService.getValue('invisible'),
            }))
            .sort((a: any, b: any) => a.top - b.top);
          return { ...data, list };
        } else {
          return { total: 0, list: [] };
        }
      })
    );
  }

  /**@getCommissionTopReal 获取实时排名 */
  getCommissionTopReal(params: TopRankParams): Observable<TopRankRepsonse> {
    const url = `${environment.apiUrl}/v1/agent/friend/commissiontopreal`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x?.data as TopRankRepsonse;
          const list = data.list
            .map(info => ({
              ...info,
              avatar: this.avatartDefault.transform(info.avatar),
              name: info.name.length > 0 ? info.name : this.localeService.getValue('invisible'),
            }))
            .sort((a: any, b: any) => a.top - b.top);
          return { ...data, list };
        } else {
          return { total: 0, list: [] };
        }
      })
    );
  }

  /**@getCommissionTopOne  顶级推荐人排行榜-个人排行情况*/
  getCommissionTopOne(): Observable<TopOne> {
    const url = `${environment.apiUrl}/v1/agent/friend/commissiontopone`;
    return this.get(url).pipe(map((x: any) => x?.data || { commission: 0, reward: 0, uId: '' }));
  }

  /**@getAllReferralsCodes 获取所有推荐码信息*/
  getAllReferralsCodes(): Promise<any> {
    const url = `${environment.apiUrl}/v1/alliance/alliance/getallreferralcodes`;
    return firstValueFrom(this.get(url));
  }

  /**@getReferralsList 获取好友信息*/
  getReferralsList(params: FirendsListParams): Observable<ReferralsListRepsonse> {
    const url = `${environment.apiUrl}/v1/alliance/alliance/getreferralslist`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const reaultData = x?.data as ReferralsListRepsonse;

          return { ...reaultData };
        } else {
          return { total: 0, list: [] };
        }
      })
    );
  }

  /**@getReferralRewardsList 获取推荐奖励列表*/
  getReferralRewardsList(params: FirendsListParams): Observable<ReferralRewardsList> {
    const url = `${environment.apiUrl}/v1/alliance/alliance/getreferralrewardslist`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x?.data as ReferralRewardsList;
          return { ...data };
        } else {
          return { total: 0, list: [] };
        }
      })
    );
  }

  /**@getCommissionRewardsList 获取佣金奖励列表*/
  getCommissionRewardsList(params: FirendsListParams): Observable<CommissionRewardsList> {
    const url = `${environment.apiUrl}/v1/alliance/alliance/getcommissionrewardslist`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          const data = x?.data as CommissionRewardsList;
          return { ...data };
        } else {
          return { total: 0, list: [] };
        }
      })
    );
  }

  /**@getCommissionRewardsInfor 获取vip奖励信息*/
  getCommissionRewardsInfor(): Promise<any> {
    const url = `${environment.apiUrl}/v1/alliance/alliance/getcommissionrewardinfo`;
    return firstValueFrom(this.get(url));
  }

  /**@getReferralInfo 获取推荐信息*/
  getReferralInfo(): Promise<any> {
    const url = `${environment.apiUrl}/v1/alliance/alliance/getreferralinfo`;
    return firstValueFrom(this.get(url));
  }

  /**@getReferralRewardsInfo 获取vip奖励信息*/
  getReferralRewardsInfo(): Promise<any> {
    const url = `${environment.apiUrl}/v1/alliance/alliance/getreferralrewardinfo`;
    return firstValueFrom(this.get(url));
  }

  /**@postCreateReferralCode 新增推荐码*/
  postCreateReferralCode(remark: string, isDefault: boolean) {
    const url = `${environment.apiUrl}/v1/alliance/alliance/createreferralcode`;
    return firstValueFrom(this.post(url, { remark, isDefault }));
  }

  /**@postDeleteReferralCode 删除推荐码*/
  postDeleteReferralCode(referralCode: string) {
    const url = `${environment.apiUrl}/v1/alliance/alliance/deletereferralcode`;
    return firstValueFrom(this.post(url, { referralCode }));
  }

  /**@postEditWelcomemessage 更改欢迎信息*/
  postEditWelcomemessage(welcomeMessage: string) {
    const url = `${environment.apiUrl}/v1/alliance/alliance/editwelcomemessage`;
    return firstValueFrom(this.post(url, { welcomeMessage }));
  }

  /**@postToggleWelcomemessage 开关欢迎信息*/
  postToggleWelcomemessage(isEnable: boolean) {
    const url = `${environment.apiUrl}/v1/alliance/alliance/togglewelcomemessage`;
    return firstValueFrom(this.post(url, { isEnable }));
  }

  /**@getWelcomemessage 获取欢迎信息*/
  getWelcomemessage(): Promise<any> {
    const url = `${environment.apiUrl}/v1/alliance/alliance/getwelcomemessage`;
    return firstValueFrom(this.get(url));
  }

  /**@postUpdatedefaultReferralCode 设置默认*/
  postUpdatedefaultReferralCode(referralCode: string) {
    const url = `${environment.apiUrl}/v1/alliance/alliance/updatedefaultreferralcode`;
    return firstValueFrom(this.post(url, { referralCode }));
  }

  /**@postRegisterNewReferral 新好友创建推荐关系*/
  postRegisterNewReferral(referralCode: string, uid: string) {
    const url = `${environment.apiUrl}/v1/alliance/alliance/registernewreferral`;
    return firstValueFrom(this.post(url, { referralCode, uid }));
  }

  /**@getShareQuery 分享页配置查询*/
  getShareQuery(params: ShareQueryParams): Observable<any> {
    const url = `${environment.apiUrl}/v1/alliance/alliance/sharequery`;
    return this.get(url, params).pipe(
      map((x: any) => {
        if (x?.data) {
          return x.data;
        }
      })
    );
  }

  /**@postUpdateReferralCodeRemark 更新推荐码备注*/
  postUpdateReferralCodeRemark(referralCode: string = '', remark: string) {
    const url = `${environment.apiUrl}/v1/alliance/alliance/updatereferralcoderemark`;
    return firstValueFrom(this.post(url, { referralCode, remark }));
  }
}
